import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

const BG = "/yd-login-bg.jpg";
const BG_GREEN = "/yd-login-green.jpg";

export default function Login() {
  const { login, register, forgotPassword } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
        navigate("/");
      } else if (mode === "forgot") {
        const res = await forgotPassword(email);
        setInfo(res?.message || "If an account exists for that email, a reset link has been sent.");
      } else {
        const res = await register(name, email, password);
        setInfo(res?.message || "Registration submitted. Your account is awaiting admin approval.");
        setMode("login");
        setPassword("");
        setName("");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="flex-1 flex items-center justify-center px-6 bg-white">
        <div className="w-full max-w-sm fade-in-up">
          <div className="flex items-center gap-2.5 mb-10">
            <img src="/yd-logo.png" alt="Yogiji Digi Ltd" className="h-11 w-11 object-contain" data-testid="login-client-logo" />
            <div>
              <p className="font-extrabold text-lg text-slate-900 leading-tight" style={{ fontFamily: "Manrope" }}>Yogiji Digi</p>
              <p className="text-[10px] uppercase tracking-widest text-slate-500">Analytics Platform</p>
            </div>
          </div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 mb-1">
            {mode === "login" ? "Welcome back" : mode === "forgot" ? "Forgot password" : "Create account"}
          </h1>
          <p className="text-sm text-slate-500 mb-8">
            {mode === "login" ? "Sign in to your analytics workspace" : mode === "forgot" ? "Enter your email and we'll send you a link to set a new password" : "Set up your finance workspace"}
          </p>
          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div className="space-y-1.5">
                <Label htmlFor="name">Full name</Label>
                <Input id="name" data-testid="register-name-input" value={name} onChange={(e) => setName(e.target.value)} required placeholder="Jane Doe" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@company.com" />
            </div>
            {mode !== "forgot" && (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                {mode === "login" && (
                  <button type="button" data-testid="forgot-password-link" onClick={() => { setMode("forgot"); setError(""); setInfo(""); }} className="text-xs text-sky-700 font-medium hover:underline">Forgot password?</button>
                )}
              </div>
              <Input id="password" data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" />
            </div>
            )}
            {info && <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2" data-testid="login-info-message">{info}</p>}
            {error && <p className="text-sm text-red-600" data-testid="login-error-message">{error}</p>}
            <Button type="submit" data-testid="login-submit-button" disabled={loading} className="w-full bg-sky-700 hover:bg-sky-800 active:scale-[0.98] transition-transform duration-200">
              {loading ? "Please wait..." : mode === "login" ? "Sign in" : mode === "forgot" ? "Send reset link" : "Create account"}
            </Button>
          </form>
          <p className="text-sm text-slate-500 mt-6">
            {mode === "forgot" ? (
              <button data-testid="back-to-login-button" onClick={() => { setMode("login"); setError(""); setInfo(""); }} className="text-sky-700 font-semibold hover:underline">← Back to sign in</button>
            ) : (<>
            {mode === "login" ? "No account?" : "Already have an account?"}{" "}
            <button data-testid="toggle-auth-mode-button" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }} className="text-sky-700 font-semibold hover:underline">
              {mode === "login" ? "Register" : "Sign in"}
            </button>
            </>)}
          </p>
        </div>
      </div>
      <div className="hidden lg:block flex-1 relative">
        <div className="absolute inset-0 flex flex-col">
          <img src={BG} alt="Yogiji Digi plant" className="h-1/2 w-full object-cover" />
          <img src={BG_GREEN} alt="Green Cold Rolling Mill" className="h-1/2 w-full object-cover" data-testid="login-hero-green" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-slate-950/10" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <h2 className="text-2xl font-bold" style={{ fontFamily: "Manrope" }} data-testid="login-hero-heading">Green Cold Rolling Mill</h2>
        </div>
      </div>
    </div>
  );
}
